import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import styles from "./View.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomButton } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const resultStatus = [
  { label: "Detained", value: "0" },
  { label: "Promoted", value: "1" },
  // { label: "Transferred", value: "2" },
];

// Helper: convert label → value
const getLabelToValue = (label) => {
  if (label?.toLowerCase() === "transferred") return "Transferred";
  const found = resultStatus.find(
    (item) => item.label.toLowerCase() === label?.toLowerCase(),
  );
  return found ? found.value : "";
};

const PublishMarkView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const userId = user?.id || "";
  const staff_id = clientData?.master_id || "";
  const school_id = clientData?.school_id || "";

  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);

  const stateData = location.state || {};
  const allVerified = stateData.allVerified || false;
  const class_id = stateData.class_id || "";
  const section_id = stateData.section_id || "";
  const config_id = stateData.config_id || "";
  const data = stateData.data || "";

  // ─── State ───
  const [resultStatusMap, setResultStatusMap] = useState({});
  const [viewData, setViewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  // ─── Fetch view data ───
  const fetchViewData = useCallback(async () => {
    if (!class_id || !section_id || !config_id) return;
    setLoading(true);
    try {
      const payload = {
        school_id: parseInt(school_id),
        staff_id: parseInt(staff_id),
        class_id: parseInt(class_id),
        section_id: section_id,
        config_id: parseInt(config_id),
        student_id: "",
        student_name: "",
        gender: "",
        total_marks: "",
        rank: "",
      };
      const response = await AxiosInstance.post(
        "/exam/examPublish/view",
        payload,
      );
      if (response?.data?.status) {
        const fetchedData = response.data.data;
        setViewData(fetchedData);

        // ── Pre-populate resultStatusMap from API result_status ──
        const initialMap = {};
        (fetchedData?.students || []).forEach((student) => {
          if (student.result_status) {
            initialMap[student.student_id] = getLabelToValue(
              student.result_status,
            );
          }
        });
        setResultStatusMap(initialMap);
      }
    } catch (error) {
      console.error("Error fetching publish view:", error);
      setViewData(null);
    } finally {
      setLoading(false);
    }
  }, [school_id, staff_id, class_id, section_id, config_id]);

  useEffect(() => {
    fetchViewData();
  }, [fetchViewData]);

  // ─── Publish Result ───
  const handlePublish = async () => {
    setPublishLoading(true);
    try {
      const payload = {
        class_id: parseInt(class_id),
        section_id: section_id,
        config_id: parseInt(config_id),
        created_by: parseInt(userId),
      };
      const response = await AxiosInstance.post(
        "/exam/examPublish/publish_result",
        payload,
      );
      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message ||
            t.publishSuccess ||
            "Result published successfully!",
          redirectPath: "/user/publish",
        });
        fetchViewData();
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message ||
            t.publishFailed ||
            "Failed to publish result.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error publishing result:", error);
      setModalState({
        open: true,
        type: "error",
        message: t.publishFailed || "Failed to publish result.",
        redirectPath: "",
      });
    } finally {
      setPublishLoading(false);
    }
  };

  // ─── Extract data ───
  const summary = viewData?.summary || {};
  const subjects = viewData?.subjects || [];
  const students = viewData?.students || [];

  const className = viewData?.class_name || "";
  const sectionName = viewData?.section_id || "";
  const examType = viewData?.exam_type || "";
  const startDate = viewData?.start_date
    ? moment(viewData.start_date).format("YYYY-MM-DD")
    : "";
  const endDate = viewData?.end_date
    ? moment(viewData.end_date).format("YYYY-MM-DD")
    : "";

  // ─── Build table data ───
  const tableData = useMemo(() => {
    return students.map((student) => {
      const row = {
        student_id: student.student_id,
        student_name: student.student_name,
        gender: student.gender
          ? student.gender.charAt(0).toUpperCase() + student.gender.slice(1)
          : "",
        class_name: className,
        section: student.section || sectionName,
        total_mark: student.total_mark || 0,
        rank: student.rank || "",
      };
      subjects.forEach((sub) => {
        const markEntry = (student.marks_details || []).find(
          (m) => m.subject_id === sub.subject_id,
        );
        row[`subject_${sub.subject_id}`] = markEntry ? markEntry.marks : 0;
      });
      return row;
    });
  }, [students, subjects, className, sectionName]);

  // ─── Filter by search ───
  const filteredData = useMemo(() => {
    if (!apiSearch) return tableData;
    const searchLower = apiSearch.toLowerCase();
    return tableData.filter(
      (item) =>
        String(item.student_id)?.toLowerCase().includes(searchLower) ||
        item.student_name?.toLowerCase().includes(searchLower) ||
        item.gender?.toLowerCase().includes(searchLower) ||
        item.rank?.toLowerCase().includes(searchLower),
    );
  }, [tableData, apiSearch]);

  // ─── Paginate ───
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination.pageIndex, pagination.pageSize]);

  const handleResultChange = (studentId, value) => {
    setResultStatusMap((prev) => ({
      ...prev,
      [studentId]: value,
    }));
  };

  // ─── Build columns ───
  const columns = useMemo(() => {
    const baseCols = [
      {
        accessorKey: "student_id",
        header: t.studentId || "Student ID",
        enableSorting: true,
        enableColumnFilter: true,
        size: 100,
      },
      {
        accessorKey: "student_name",
        header: t.studentName || "Student Name",
        enableSorting: true,
        enableColumnFilter: true,
        size: 140,
      },
      {
        accessorKey: "gender",
        header: t.gender || "Gender",
        enableSorting: true,
        enableColumnFilter: true,
        size: 80,
      },
      {
        accessorKey: "class_name",
        header: t.class || "Class",
        enableSorting: true,
        enableColumnFilter: true,
        size: 70,
      },
      {
        accessorKey: "section",
        header: t.section || "Section",
        enableSorting: true,
        enableColumnFilter: true,
        size: 70,
      },
      {
        accessorKey: "result_status",
        header: "Result",
        enableSorting: false,
        enableColumnFilter: true,
        Cell: ({ row }) => {
          const studentId = row.original.student_id;
          const currentValue = resultStatusMap[studentId] ?? "";
          const studentClassName = row.original.class_name;

          // If Transferred and class is X or XII, always show as plain text
          if (
            currentValue === "Transferred" &&
            (studentClassName === "X" || studentClassName === "XII")
          ) {
            return <span className="">Transferred</span>;
          }

          // If rollovered, show as read-only text
          if (viewData?.is_rollovered === 1) {
            const matchedOption = resultStatus.find(
              (opt) => opt.value === currentValue,
            );
            return <span>{matchedOption?.label || currentValue || "-"}</span>;
          }

          return (
            <select
              className="form-select"
              value={currentValue}
              onChange={(e) => handleResultChange(studentId, e.target.value)}
            >
              <option value="">Select</option>
              {resultStatus.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );
        },
      },
    ];

    const subjectCols = subjects.map((sub) => ({
      accessorKey: `subject_${sub.subject_id}`,
      header: sub.subject_name,
      enableSorting: true,
      enableColumnFilter: true,
      size: 110,
    }));

    const endCols = [
      {
        accessorKey: "total_mark",
        header: t.totalMark || "Total Mark",
        enableSorting: true,
        enableColumnFilter: true,
        size: 100,
      },
      {
        accessorKey: "rank",
        header: t.rank || "Rank",
        enableSorting: true,
        enableColumnFilter: true,
        size: 80,
        Cell: ({ row }) => {
          const rank = row.original.rank;
          const isPass = rank && rank.toLowerCase() !== "fail";
          return (
            <span className={isPass ? styles.rankPass : styles.rankFail}>
              {rank}
            </span>
          );
        },
      },
    ];

    return [...baseCols, ...subjectCols, ...endCols];
  }, [subjects, t, resultStatusMap]);

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  const handleAnnualPublish = async () => {
    const unselectedStudents = students.filter(
      (student) =>
        !resultStatusMap[student.student_id] &&
        resultStatusMap[student.student_id] !== "Transferred",
    );

    if (unselectedStudents.length > 0) {
      setModalState({
        open: true,
        type: "error",
        message:
          t.selectResultStatusForAll ||
          `Please select result status for all students. ${unselectedStudents.length} student(s) remaining.`,
        redirectPath: "",
      });
      return;
    }

    try {
      setLoading(true);

      const student_details = Object.keys(resultStatusMap).map((studentId) => {
        const statusValue = resultStatusMap[studentId];
        const statusLabel =
          statusValue === "Transferred"
            ? "Transferred"
            : resultStatus.find((item) => item.value === statusValue)?.label;
        return {
          student_id: studentId,
          promotion_status: statusLabel,
        };
      });

      const payload = {
        academic_year: parseInt(clientData?.academic_year_id),
        school_id: parseInt(school_id),
        class_id: parseInt(class_id),
        section_id: section_id,
        student_details,
        updated_by: parseInt(staff_id),
      };

      const response = await AxiosInstance.post(
        "/exam/examPublish/promotionStatus",
        payload,
      );

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response?.data?.message || "Promotion status updated successfully",
          redirectPath: "/user/publish",
        });
      }
    } catch (error) {
      console.error("Promotion API error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* ─── Top Header Bar ─── */}
        <div className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <h1 className={styles.examTitle}>{examType}</h1>
          </div>
          <div className={styles.topBarRight}>
            <span className={styles.topBarInfo}>
              {t.class || "Class"}: {className}
            </span>
            <span className={styles.topBarDivider}>|</span>
            <span className={styles.topBarInfo}>
              {t.section || "Section"}: {sectionName}
            </span>
            <span className={styles.topBarDivider}>|</span>
            <span className={styles.topBarInfo}>
              {t.dates || "Dates"}: {startDate} - {endDate}
            </span>
            <span className={styles.topBarDivider}>|</span>
            <CustomButton
              variant="contained"
              onClick={handlePublish}
              className={styles.publishButton}
              loading={publishLoading}
              loadingText="..."
              disabled={data.publish_marks === 1 || allVerified}
            >
              {t.publish || "PUBLISH"}
            </CustomButton>
          </div>
        </div>

        {/* ─── Summary Cards ─── */}
        <div className={styles.summaryRow}>
          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.totalStudents || "Total Students"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.total_students || 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.total_gender?.boys || 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.total_gender?.girls || 0}
              </span>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.studentsPassed || "Students Passed"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.students_passed || 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_passed?.boys || 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_passed?.girls || 0}
              </span>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.passedPercentage || "Passed Percentage"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.pass_percentage ?? 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_pass_percentage?.boys ?? 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_pass_percentage?.girls ?? 0}
              </span>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.studentsFailed || "Students Failed"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.students_failed || 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_failed?.boys || 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_failed?.girls || 0}
              </span>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.failedPercentage || "Failed Percentage"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.fail_percentage ?? 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_fail_percentage?.boys ?? 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_fail_percentage?.girls ?? 0}
              </span>
            </div>
          </div>
        </div>

        {/* ─── Table ─── */}
        <div className={styles.tableSection}>
          <CustomMRTTable
            columns={columns}
            data={paginatedData}
            loading={loading}
            enablePagination={true}
            enableFiltering={true}
            enableSearch={true}
            enableSorting={true}
            pagination={pagination}
            rowCount={filteredData.length}
            onPaginationChange={setPagination}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            onSearchEnter={(value) => {
              setApiSearch(value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          />
        </div>
        {/* ─── Buttons ─── */}
        <div className={styles.buttonGroup}>
          <CustomButton
            variant="outlined"
            onClick={() => navigate("/user/publish")}
            className={styles.backButton}
          >
            {t.back || "BACK"}
          </CustomButton>
          <CustomButton
            variant="contained"
            onClick={handleAnnualPublish}
            className={styles.saveButton}
            loading={loading}
            style={{
              opacity:
                viewData?.is_rollovered === 1 || viewData === null ? 0.5 : 1,
              cursor:
                viewData?.is_rollovered === 1 || viewData === null
                  ? "not-allowed"
                  : "pointer",
            }}
            disabled={viewData?.is_rollovered === 1 || viewData === null}
          >
            {translations?.labels?.updatePromotionStatus ||
              "Update Promotion Status"}
          </CustomButton>
        </div>
      </div>
    </>
  );
};

export default PublishMarkView;
